export type ScopedSite = {
  org_id: string;
  site_id: string;
  display_name?: string | null;
};

export type SiteScopedDevice = {
  org_id?: string | null;
  site_id?: string | null;
  area_id?: string | null;
  area_name?: string | null;
  placement_label?: string | null;
  location?: string | null;
};

export function resolveDeviceSiteDisplay(
  device: SiteScopedDevice,
  sites: ScopedSite[],
  activeOrgId: string,
  activeSiteId = ''
): { label: string; siteId: string } {
  const siteId = String(device.site_id || activeSiteId).trim();
  const deviceOrgId = String(device.org_id || activeOrgId).trim();
  const site = deviceOrgId === activeOrgId
    ? sites.find((candidate) => (
        candidate.org_id === activeOrgId && candidate.site_id === siteId
      ))
    : undefined;
  const displayName = String(site?.display_name || '').trim();
  const siteLabel = displayName || siteId || 'Site unavailable';
  const area = String(device.area_name || device.area_id || '').trim();
  return { label: area ? `${siteLabel} / ${area}` : siteLabel, siteId };
}

export function meaningfulLegacyPlacement(value?: string | null): string {
  const placement = String(value || '').trim();
  return /^(n\/a|unknown|none|null|-)$/i.test(placement) ? '' : placement;
}

export function resolveEdgePlacementDisplay(device: SiteScopedDevice): string {
  return (
    meaningfulLegacyPlacement(device.placement_label)
    || meaningfulLegacyPlacement(device.location)
    || ''
  );
}

export type LocationSnapshotLike = {
  path?: string | null;
  site?: { name?: string | null; id?: string | null } | null;
  area?: { name?: string | null; id?: string | null } | null;
  placement?: { name?: string | null } | null;
};

export function resolveIncidentLocationPath(source: {
  location_snapshot?: LocationSnapshotLike | null;
  envelope_metadata?: { location_snapshot?: LocationSnapshotLike | null } | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
} | null | undefined): string {
  if (!source) return '';
  const snap = source.location_snapshot || source.envelope_metadata?.location_snapshot || null;
  const path = meaningfulLegacyPlacement(snap?.path);
  if (path) {
    // Customer review chrome: strip Demo* / forbid-class segments from snapshot paths.
    return sanitizeCustomerLocationPath(path);
  }
  const parts = [
    meaningfulLegacyPlacement(snap?.site?.name)
      || (isCustomerFacingLocationSegment(snap?.site?.id) ? meaningfulLegacyPlacement(snap?.site?.id) : ''),
    meaningfulLegacyPlacement(snap?.area?.name)
      || (isCustomerFacingLocationSegment(snap?.area?.id) ? meaningfulLegacyPlacement(snap?.area?.id) : ''),
    meaningfulLegacyPlacement(snap?.placement?.name),
  ]
    .map((part) => sanitizeCustomerLocationPath(part) || (isCustomerFacingLocationSegment(part) ? part : ''))
    .filter(Boolean);
  if (parts.length) return parts.join(' / ');
  // Do not interpolate Zone/Camera/Target raw ids for customer Location chrome.
  // Callers that still need technical fallbacks should use Technical details.
  return '';
}

/** Policy / CV placeholders that must never stand alone as Location. */
const NON_PHYSICAL_LOCATION_LABELS = new Set([
  'configured restricted area',
  'configured restricted zone',
  'the configured work area',
  'selected area',
  'the selected area',
  'not in scope for this hazard class',
  'location not recorded',
  'monitored zone',
  'not recorded',
  'n/a',
  'unknown',
  'none',
  'null',
  '-',
  '—',
]);

/**
 * Same forbid class as Overview Actions `OVERVIEW_FORBIDDEN_CUSTOMER_ID`.
 * Demo/test/canary/cam_* / hash tokens must never appear in customer Location.
 */
export const LOCATION_FORBIDDEN_CUSTOMER_ID =
  /cam[_-][a-z0-9]+|\bdemo\b|_demo\b|\bdemo_|demo[a-z0-9_]|[a-z0-9]demo\b|\btest\b|_test\b|\btest_|canary|[a-f0-9]{12}/i;

export function isPhysicalLocationLabel(value?: string | null): boolean {
  const label = String(value || '').trim();
  if (!label) return false;
  if (NON_PHYSICAL_LOCATION_LABELS.has(label.toLowerCase())) return false;
  if (/^cam[_-]/i.test(label)) return false;
  // Underscored/hyphenated technical IDs (demo5acc-style, worksafety_lane).
  if (/^[a-z0-9]+(?:[_-][a-z0-9]+)+$/i.test(label)) return false;
  // All-lowercase single tokens are usually CV zone slugs (worksafety), not place names.
  if (/^[a-z][a-z0-9]*$/.test(label)) return false;
  return true;
}

/** True when a path/mount/secondary segment is safe for customer Location chrome. */
export function isCustomerFacingLocationSegment(value?: string | null): boolean {
  const label = String(value || '').trim();
  if (!label) return false;
  if (!isPhysicalLocationLabel(label)) return false;
  if (LOCATION_FORBIDDEN_CUSTOMER_ID.test(label)) return false;
  return true;
}

/** Drop forbid-class segments from a slash-joined location path. */
export function sanitizeCustomerLocationPath(value?: string | null): string {
  const path = meaningfulLegacyPlacement(value);
  if (!path) return '';
  const parts = path
    .split(/\s*\/\s*/)
    .map((part) => String(part || '').trim())
    .filter((part) => isCustomerFacingLocationSegment(part));
  return parts.join(' / ');
}

export type CameraPlacementLookup = {
  areaId?: string | null;
  areaName?: string | null;
  mountLabel?: string | null;
  path?: string | null;
  deviceId?: string | null;
};

/** Canonical placement map key: device_id+camera_id when both exist. */
export function cameraPlacementMapKey(
  deviceId?: string | null,
  cameraId?: string | null,
): string | null {
  const camera = String(cameraId || '').trim();
  if (!camera) return null;
  const device = String(deviceId || '').trim();
  return device ? `${device}::${camera}` : camera;
}

/**
 * Resolve a placement using device+camera first, then a unique camera_id alias.
 * Does not invent Area identity from zone, mount, or location text.
 */
export function resolveIndexedCameraPlacement<T>(
  lookups: Record<string, T> | undefined,
  args: { cameraId?: string | null; deviceId?: string | null },
): T | undefined {
  if (!lookups) return undefined;
  const cameraId = String(args.cameraId || '').trim();
  const deviceId = String(args.deviceId || '').trim();
  if (cameraId && deviceId) {
    const qualified = lookups[`${deviceId}::${cameraId}`];
    if (qualified) return qualified;
  }
  if (cameraId) return lookups[cameraId];
  return undefined;
}

export function indexCameraPlacementLookups(
  placements: Array<{
    camera_id?: string | null;
    device_id?: string | null;
    area_id?: string | null;
    area_name?: string | null;
    mount_label?: string | null;
    path?: string | null;
  }>,
): Record<string, CameraPlacementLookup> {
  const byId: Record<string, CameraPlacementLookup> = {};
  const cameraIdOwner = new Map<string, string>();
  for (const placement of placements || []) {
    const cameraId = String(placement.camera_id || '').trim();
    if (!cameraId) continue;
    const deviceId = String(placement.device_id || '').trim();
    const lookup: CameraPlacementLookup = {
      areaId: placement.area_id,
      areaName: placement.area_name,
      mountLabel: placement.mount_label,
      path: placement.path,
      deviceId: deviceId || null,
    };
    const qualified = cameraPlacementMapKey(deviceId, cameraId);
    if (qualified && qualified !== cameraId) byId[qualified] = lookup;
    const owner = cameraIdOwner.get(cameraId);
    if (owner == null) {
      cameraIdOwner.set(cameraId, deviceId);
      byId[cameraId] = lookup;
    } else if (owner !== deviceId) {
      delete byId[cameraId];
    }
  }
  return byId;
}

export type SafetyLocationLookups = {
  siteDisplayName?: string | null;
  siteId?: string | null;
  cameraPlacementById?: Record<string, CameraPlacementLookup>;
};

export type SafetyLocationSource = {
  location_snapshot?: LocationSnapshotLike | null;
  envelope_metadata?: { location_snapshot?: LocationSnapshotLike | null } | null;
  camera_id?: string | null;
  zone_id?: string | null;
  zoneLabel?: string | null;
  areaLabel?: string | null;
  siteLabel?: string | null;
  cameraName?: string | null;
};

export type SafetyLocationDisplay = {
  primary: string;
  secondary: string;
  /** Single-line label for tables that only show one string. */
  label: string;
};

function buildRegistryPath(
  lookups: SafetyLocationLookups | undefined,
  cameraId?: string | null,
): string {
  const cam = String(cameraId || '').trim();
  const placement = cam ? lookups?.cameraPlacementById?.[cam] : undefined;
  if (!placement) return '';
  const explicit = sanitizeCustomerLocationPath(placement.path);
  if (explicit) return explicit;
  const site = isCustomerFacingLocationSegment(lookups?.siteDisplayName)
    ? meaningfulLegacyPlacement(lookups?.siteDisplayName)
    : (isCustomerFacingLocationSegment(lookups?.siteId)
      ? meaningfulLegacyPlacement(lookups?.siteId)
      : '');
  const area = isCustomerFacingLocationSegment(placement.areaName)
    ? meaningfulLegacyPlacement(placement.areaName)
    : (isCustomerFacingLocationSegment(placement.areaId)
      ? meaningfulLegacyPlacement(placement.areaId)
      : '');
  // mountLabel is never raw customer-facing when demo/internal (forbid class).
  const mount = isCustomerFacingLocationSegment(placement.mountLabel)
    ? meaningfulLegacyPlacement(placement.mountLabel)
    : '';
  return [site, area, mount].filter(Boolean).join(' / ');
}

function secondaryCandidate(
  source: SafetyLocationSource,
  primary: string,
): string {
  const primaryNorm = primary.trim().toLowerCase();
  const cameraName = meaningfulLegacyPlacement(source.cameraName);
  if (
    cameraName
    && cameraName.toLowerCase() !== primaryNorm
    && !primaryNorm.includes(cameraName.toLowerCase())
    && isCustomerFacingLocationSegment(cameraName)
  ) {
    return cameraName;
  }
  const candidates = [
    meaningfulLegacyPlacement(source.zoneLabel),
    meaningfulLegacyPlacement(source.areaLabel),
  ].filter(Boolean);
  for (const candidate of candidates) {
    if (candidate.toLowerCase() === primaryNorm) continue;
    if (primaryNorm.includes(candidate.toLowerCase())) continue;
    if (!isCustomerFacingLocationSegment(candidate)) continue;
    return candidate;
  }
  return '';
}

/**
 * Product rule for Safety surfaces:
 * primary = physical path (snapshot or registry placement);
 * secondary = camera nickname / CV zone / policy label.
 */
export function resolveSafetyLocationDisplay(
  source: SafetyLocationSource | null | undefined,
  lookups: SafetyLocationLookups = {},
): SafetyLocationDisplay {
  const empty: SafetyLocationDisplay = {
    primary: 'Location not recorded',
    secondary: '',
    label: 'Location not recorded',
  };
  if (!source) return empty;

  const snapPath = resolveIncidentLocationPath({
    location_snapshot: source.location_snapshot,
    envelope_metadata: source.envelope_metadata,
  });
  // resolveIncidentLocationPath already sanitizes Demo*/forbid-class segments.
  const snapshotPrimary = sanitizeCustomerLocationPath(snapPath);
  const registryPrimary = buildRegistryPath(lookups, source.camera_id);
  const siteName = (
    isCustomerFacingLocationSegment(lookups.siteDisplayName)
      ? meaningfulLegacyPlacement(lookups.siteDisplayName)
      : ''
  )
    || (
      isCustomerFacingLocationSegment(source.siteLabel)
        ? meaningfulLegacyPlacement(source.siteLabel)
        : ''
    )
    || (
      isCustomerFacingLocationSegment(lookups.siteId)
        ? meaningfulLegacyPlacement(lookups.siteId)
        : ''
    );
  const areaPart = isCustomerFacingLocationSegment(source.areaLabel)
    ? meaningfulLegacyPlacement(source.areaLabel)
    : '';
  // Site alone is not enough to override a known room/zone label; require area.
  const siteAreaFallback = areaPart
    ? [siteName, areaPart].filter(Boolean).join(' / ')
    : '';

  const primary = snapshotPrimary
    || registryPrimary
    || (isCustomerFacingLocationSegment(siteAreaFallback) ? siteAreaFallback : '')
    || (isCustomerFacingLocationSegment(source.areaLabel) ? String(source.areaLabel).trim() : '')
    || (isCustomerFacingLocationSegment(source.zoneLabel) ? String(source.zoneLabel).trim() : '')
    || (isCustomerFacingLocationSegment(siteName) ? siteName : '')
    || 'Location not recorded';

  const secondary = secondaryCandidate(source, primary);
  const label = secondary && primary !== 'Location not recorded'
    ? `${primary} · ${secondary}`
    : primary;
  return { primary, secondary, label };
}

export function formatSafetyLocationPrimary(
  source: SafetyLocationSource | null | undefined,
  lookups: SafetyLocationLookups = {},
): string {
  return resolveSafetyLocationDisplay(source, lookups).primary;
}
